import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Handshake, Users, CheckCircle, MapPin, Calendar, Storefront, Buildings, UserCircle, Briefcase, Wallet, Coins, Receipt } from "@phosphor-icons/react";
import axios from "axios";

const TYPE_ICON = { LSGD: Buildings, Retail: Storefront, Staff: UserCircle, ServiceProvider: Briefcase };

export default function PublicPartner() {
  const { partnerId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    document.title = "Partner Profile · Rescigo Health";
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${apiUrl}/api/public/partners/${partnerId}`)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Partner not found"));
  }, [partnerId]);

  if (err) return (
    <div className="min-h-screen bg-[#F7F6F2] flex items-center justify-center p-6">
      <div className="bg-white border border-[#E5E2D9] rounded-md p-10 text-center max-w-md">
        <div className="text-5xl mb-3">🤷</div>
        <div className="font-heading text-xl mb-1">Partner not found</div>
        <div className="text-sm text-[#5C5A55]">{err}</div>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-[#F7F6F2] flex items-center justify-center text-sm text-[#5C5A55]">Loading…</div>;

  const Icon = TYPE_ICON[data.partner.type] || Handshake;

  return (
    <div className="min-h-screen bg-[#F7F6F2]" data-testid="public-partner">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#2D5A27] to-[#3F7A37] text-white">
        <div className="max-w-5xl mx-auto px-6 py-10">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] opacity-80">
            <Handshake size={12} weight="fill"/> Rescigo Health · Partner Program
          </div>
          <div className="mt-3 flex items-start gap-4">
            <div className="w-16 h-16 bg-white/15 rounded-full flex items-center justify-center backdrop-blur-sm shrink-0">
              <Icon size={32} weight="fill"/>
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-3xl sm:text-4xl truncate" data-testid="pp-name">{data.partner.name}</h1>
              <div className="mt-1 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] opacity-90 bg-white/15 px-2.5 py-1 rounded">
                {data.partner.type}
              </div>
              {data.partner.joined_at && (
                <div className="text-xs opacity-80 mt-2 inline-flex items-center gap-1.5">
                  <Calendar size={11}/> Partner since {new Date(data.partner.joined_at).toLocaleDateString()}
                </div>
              )}
            </div>
            {!data.partner.active && (
              <div className="text-[10px] uppercase tracking-wider bg-[#7A2E18] px-2.5 py-1 rounded">Inactive</div>
            )}
          </div>

          {/* Stats */}
          <div className="mt-8 grid grid-cols-2 gap-3 max-w-md">
            <Stat label="Customers Onboarded" value={data.stats.customers_total} icon={Users}/>
            <Stat label="Currently Active" value={data.stats.customers_active} icon={CheckCircle}/>
          </div>
        </div>
      </div>

      {/* Wallet & Payouts */}
      <div className="max-w-5xl mx-auto px-6 -mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4 z-10 relative" data-testid="pp-wallet-section">
        <div className="bg-white border border-[#E5E2D9] rounded-md p-4 shadow-sm">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">
            <Wallet size={11} weight="fill" className="text-[#2D5A27]"/> Total Earned
          </div>
          <div className="text-2xl font-light tabular-nums mt-1">₹{Number(data.wallet?.total_earned||0).toFixed(2)}</div>
          <div className="text-[10px] text-[#8A8780]">
            {data.partner.commission_enabled
              ? `Commission ${Math.round((data.partner.commission_pct||0.10) * 100)}% on ${(data.partner.commission_base||"package_price") === "package_price" ? "package price" : "invoice total"}`
              : "Commission currently disabled"}
          </div>
        </div>
        <div className="bg-white border-2 border-[#2D5A27] rounded-md p-4 shadow-sm">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#2D5A27]">
            <Coins size={11} weight="fill"/> Available Balance
          </div>
          <div className="text-2xl font-light tabular-nums mt-1 text-[#2D5A27]">₹{Number(data.wallet?.available||0).toFixed(2)}</div>
          <div className="text-[10px] text-[#8A8780]">Awaiting next payout</div>
        </div>
        <div className="bg-white border border-[#E5E2D9] rounded-md p-4 shadow-sm">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">
            <Receipt size={11} weight="fill" className="text-[#2D5A27]"/> Disbursed
          </div>
          <div className="text-2xl font-light tabular-nums mt-1">₹{Number(data.wallet?.total_paid||0).toFixed(2)}</div>
          <div className="text-[10px] text-[#8A8780]">{data.payouts?.length || 0} payouts to date</div>
        </div>
      </div>

      {/* Payout history */}
      <div className="max-w-5xl mx-auto px-6 mt-6">
        <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden" data-testid="pp-payouts-section">
          <div className="px-5 py-3 border-b border-[#E5E2D9] flex items-center gap-2">
            <Receipt size={14}/>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">Disbursement History</div>
            <div className="ml-auto text-[11px] text-[#8A8780]">{data.payouts?.length || 0} payouts</div>
          </div>
          {(!data.payouts || data.payouts.length === 0) ? (
            <div className="p-8 text-center text-sm text-[#8A8780]">
              <Receipt size={26} className="inline-block mb-2 opacity-40"/><br/>
              No payouts disbursed yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F7F6F2] text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">
                  <tr>
                    <th className="text-right px-5 py-2.5">Amount</th>
                    <th className="text-left px-3 py-2.5">Method</th>
                    <th className="text-left px-3 py-2.5">UPI Ref</th>
                    <th className="text-left px-3 py-2.5">Notes</th>
                    <th className="text-right px-5 py-2.5">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.payouts.map((po, i) => (
                    <tr key={i} className="border-t border-[#F0ECE3]">
                      <td className="px-5 py-2.5 text-right font-mono text-[#2D5A27] font-semibold">₹{Number(po.amount||0).toFixed(2)}</td>
                      <td className="px-3 py-2.5">
                        <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.18em] bg-[#D4E0D1] text-[#2D5A27]">{po.method}</span>
                      </td>
                      <td className="px-3 py-2.5 font-mono text-[11px] text-[#5C5A55]">{po.upi_txn_id_masked || "—"}</td>
                      <td className="px-3 py-2.5 text-[11px] text-[#5C5A55] truncate max-w-[200px]">{po.notes || "—"}</td>
                      <td className="px-5 py-2.5 text-right text-[11px] text-[#8A8780]">{po.paid_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Customers list */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden" data-testid="pp-customers">
          <div className="px-5 py-3 border-b border-[#E5E2D9] flex items-center gap-2">
            <Users size={14}/>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">Onboarded Customers</div>
            <div className="ml-auto text-[11px] text-[#8A8780]">{data.customers.length} total</div>
          </div>
          {data.customers.length === 0 ? (
            <div className="p-12 text-center text-sm text-[#8A8780]">
              <Users size={28} className="inline-block mb-2 opacity-40"/><br/>
              No customers onboarded yet via this partner.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F7F6F2] text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">
                  <tr>
                    <th className="text-left px-5 py-2.5">Code</th>
                    <th className="text-left px-3 py-2.5">Customer</th>
                    <th className="text-left px-3 py-2.5">Phone</th>
                    <th className="text-left px-3 py-2.5">LSGD · Ward</th>
                    <th className="text-left px-3 py-2.5">Status</th>
                    <th className="text-right px-5 py-2.5">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {data.customers.map((c, i) => (
                    <tr key={c.customer_code+i} className="border-t border-[#F0ECE3]">
                      <td className="px-5 py-2.5 font-mono text-[11px] text-[#2D5A27]">{c.customer_code}</td>
                      <td className="px-3 py-2.5">{c.name_initial}</td>
                      <td className="px-3 py-2.5 font-mono text-[11px] text-[#5C5A55]">{c.phone_masked}</td>
                      <td className="px-3 py-2.5 text-[#5C5A55] inline-flex items-center gap-1">
                        <MapPin size={10}/> {c.lsgd_name} · {c.ward}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`text-[9px] px-2 py-0.5 rounded uppercase tracking-wider ${
                          c.status === "active" || c.status === "approved" ? "bg-[#D4E0D1] text-[#1A3317]" :
                          c.status === "pending_verification" ? "bg-[#F9E4B7] text-[#7A5A11]" :
                          "bg-[#F4C7B9] text-[#7A2E18]"
                        }`}>{(c.status || "—").replace(/_/g," ")}</span>
                      </td>
                      <td className="px-5 py-2.5 text-right text-[11px] text-[#8A8780]">
                        {c.joined_at ? new Date(c.joined_at).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-6 text-center text-[11px] text-[#8A8780]">
          Phone numbers and identities are masked for privacy.<br/>
          For full reports & payouts, please contact your Rescigo account manager.
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, icon: I }) => (
  <div className="bg-white/10 backdrop-blur-sm rounded-md p-4 border border-white/10">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] opacity-80">
      <I size={11}/> {label}
    </div>
    <div className="text-3xl font-light mt-1.5 tabular-nums">{value}</div>
  </div>
);
